import React, { useMemo, useRef, useState } from "react";
import { Download, FileSpreadsheet, History, Upload } from "lucide-react";
import { toast } from "react-toastify";
import AppShell from "../layout/AppShell";
import { ENV, tbGetJson, tbPostJson } from "../api/client";
import NPWBulkUploadHistoryModal from "./NPWBulkUploadHistoryModal";
import "./NPWBulkUpload.css";

const ALLOWED_ROLES = ["Nominator", "Employee Sponsor", "Employee Supplier Admin", "DTV OPS Admin"];
const NETWORK_ERROR_MESSAGE = "Something went wrong, please try again";
const clean = (value) => String(value ?? "").trim();

export function isValidDownloadUrl(value) {
    if (typeof value !== "string") return false;
    const trimmed = value.trim();
    if (!trimmed) return false;
    if (!/^https?:\/\//i.test(trimmed)) return false;
    if (/javascript:/i.test(trimmed) || /data:/i.test(trimmed)) return false;
    try {
        const parsed = new URL(trimmed);
        return parsed.protocol === "http:" || parsed.protocol === "https:";
    } catch {
        return false;
    }
}

export function extractTemplateDownloadUrl(payload) {
    const candidates = [];

    const push = (value) => {
        if (value == null) return;
        if (typeof value === "string") {
            const trimmed = value.trim();
            if (trimmed) candidates.push(trimmed);
            return;
        }
        if (typeof value === "object") {
            Object.values(value).forEach((nested) => push(nested));
        }
    };

    if (typeof payload === "string") {
        push(payload);
    } else if (payload && typeof payload === "object") {
        const direct = payload.data && typeof payload.data === "object" ? payload.data : payload;
        push(direct?.downloadUrl);
        push(direct?.download_url);
        push(direct?.url);
        push(direct?.fileUrl);
        push(direct?.file_url);
        push(direct?.link);
        push(direct?.templateUrl);
        push(direct?.template_url);
        push(payload?.downloadUrl);
        push(payload?.download_url);
        push(payload?.url);
        push(payload?.link);
        push(payload?.data);
    }

    for (const candidate of candidates) {
        if (isValidDownloadUrl(candidate)) return candidate;
    }

    throw new Error("Template download link is missing or invalid in the API response.");
}

export function getTemplateDownloadState(payload, now = Date.now()) {
    const url = extractTemplateDownloadUrl(payload);
    const rawExpiresInSeconds = Number(
        payload?.expiresInSeconds ??
        payload?.expires_in_seconds ??
        payload?.data?.expiresInSeconds ??
        payload?.data?.expires_in_seconds ??
        0
    );

    const expiresInSeconds = Number.isFinite(rawExpiresInSeconds) && rawExpiresInSeconds > 0
        ? rawExpiresInSeconds
        : 0;

    const expiresAt = expiresInSeconds > 0 ? now + (expiresInSeconds * 1000) : 0;

    return {
        url,
        expiresInSeconds,
        expiresAt,
        isExpired: expiresAt > 0 ? now >= expiresAt : false,
    };
}

function responseMessage(error) {
    const body = error?.body;
    if (body && typeof body === "object") return clean(body.message) || clean(body.error);
    return clean(error?.message);
}

function endpoint(configured, base, path) {
    return configured || (base ? `${base.replace(/\/$/, "")}/${path}` : `/${path}`);
}

export default function NPWBulkUpload() {
    const fileInputRef = useRef(null);
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);
    const [notice, setNotice] = useState(null);
    const [templateLoadError, setTemplateLoadError] = useState("");
    const [loadingTemplate, setLoadingTemplate] = useState(false);
    const [historyOpen, setHistoryOpen] = useState(false);

    const profile = useMemo(() => ({
        submitterId: clean(sessionStorage.getItem("tb_email")),
        submitterName: clean(sessionStorage.getItem("tb_name")),
        submitterDtvid: clean(sessionStorage.getItem("tb_dtvid")),
        submitterRole: clean(sessionStorage.getItem("tb_role")),
        // vendorName captured at login lookup (RequireAuth.jsx) and cached in session
        vendorName: clean(sessionStorage.getItem("tb_loginVendorSupplierName")),
    }), []);
// alert(profile.vendorName);
    const showNotice = (message, type) => {
        const text = clean(message) || NETWORK_ERROR_MESSAGE;
        setNotice({ text, type });
        toast(text, { type, containerId: "global" });
    };

    const canUpload = Boolean(file && /\.xlsx$/i.test(file.name) && ALLOWED_ROLES.includes(profile.submitterRole) && profile.submitterId && profile.submitterName && profile.submitterDtvid && profile.submitterRole);

    const downloadTemplate = async () => {
        if (loadingTemplate) return;
        setLoadingTemplate(true);
        setTemplateLoadError("");
        try {
            const apiUrl = ENV.BULK_UPLOAD_TEMPLATE_DOWNLOAD_URL || `${ENV.TB_BASE}/api/v1/bulkupload-template-download?download=true`;
            const response = await tbGetJson(apiUrl, { credentials: "include" });
            const resolvedUrl = extractTemplateDownloadUrl(response);

            const link = document.createElement("a");
            link.href = resolvedUrl;
            link.download = "npw-bulk-upload-template.xlsx";
            link.target = "_blank";
            link.rel = "noopener noreferrer";
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            const message = responseMessage(error) || "Failed to load the bulk upload template download link.";
            setTemplateLoadError(message);
            toast.error(message, { containerId: "global" });
        } finally {
            setLoadingTemplate(false);
        }
    };

    const upload = () => {
        if (!canUpload || busy) return;
        setBusy(true);
        const reader = new FileReader();
        reader.onload = async () => {
            try {
                const contentBase64 = String(reader.result || "").replace(/^data:.*?;base64,/, "");
                const isNominator = profile.submitterRole === "Nominator";
                const { vendorName, ...restProfile } = profile;
                const requestBody = {
                    fileName: file.name,
                    contentBase64,
                    ...restProfile,
                    ...(isNominator ? { vendorName } : {}),
                };
                const data = await tbPostJson(endpoint(ENV.BULK_UPLOAD_URL, ENV.TB_BASE, "bulkupload-nomination"), requestBody);
                showNotice(data?.message, "success");
                setFile(null);
                if (fileInputRef.current) fileInputRef.current.value = "";
            } catch (error) {
                showNotice(responseMessage(error), "error");
            } finally {
                setBusy(false);
            }
        };
        reader.onerror = () => { showNotice("", "error"); setBusy(false); };
        reader.readAsDataURL(file);
    };

    return (
        <AppShell activeKey="bulk-upload">
            <section className="bulk-upload-page">
                <div className="bulk-upload-heading">
                    <div><p className="bulk-upload-kicker">NPW onboarding</p><h1>Bulk Upload</h1><p>Upload the completed workbook for background processing.</p><b>Maximum 2,000 rows/record allowed per upload.</b></div>
                    <div className="bulk-heading-actions">
                        <button className="bulk-secondary-button" type="button" onClick={() => setHistoryOpen(true)}>
                            <History size={17} aria-hidden="true" />
                            Uploaded file history
                        </button>
                        <button className="bulk-secondary-button" type="button" onClick={downloadTemplate} disabled={loadingTemplate}>
                            <Download size={17} aria-hidden="true" />
                            {loadingTemplate ? "Loading template..." : "Download template"}
                        </button>
                    </div>
                </div>
                <div className="bulk-upload-layout">
                    <div className="bulk-upload-panel">
                        <div className="bulk-panel-title"><FileSpreadsheet size={20} aria-hidden="true" /><h2>Choose workbook</h2></div>
                        <label className="bulk-dropzone" htmlFor="npw-bulk-file"><Upload size={28} aria-hidden="true" /><strong>{file ? file.name : "Select an .xlsx file"}</strong><span>{file ? `${Math.ceil(file.size / 1024)} KB` : "The completed template is ready to upload"}</span></label>
                        <input ref={fileInputRef} id="npw-bulk-file" type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={(event) => { setFile(event.target.files?.[0] || null); setTemplateLoadError(""); setNotice(null); }} />
                        <button className="bulk-primary-button" type="button" onClick={upload} disabled={!canUpload || busy}><Upload size={17} aria-hidden="true" />{busy ? "Uploading..." : "Upload workbook"}</button>
                        {templateLoadError && <div className="bulk-inline-notice error" role="status">{templateLoadError}</div>}
                        {notice && <div className={`bulk-inline-notice ${notice.type}`} role="status">{notice.text}</div>}
                    </div>
                    <aside className="bulk-info-panel"><span className="bulk-info-label">Submission</span><h2>Background processing</h2><p>Your file is submitted for row-by-row validation and onboarding. The processing summary and any failed-row workbook are sent by email.</p><dl><div><dt>Submitter</dt><dd>{profile.submitterName || "-"}</dd></div><div><dt>Role</dt><dd>{profile.submitterRole || "-"}</dd></div></dl></aside>
                </div>
            </section>
            <NPWBulkUploadHistoryModal open={historyOpen} onClose={() => setHistoryOpen(false)} profile={profile} />
        </AppShell>
    );
}